import { useEffect } from 'react';
import type { HeroSlotData, SchemaOrgType } from '@/types/theme';

interface Props {
  schemaTypes: SchemaOrgType[];
  hero?: HeroSlotData | null;
  canonicalUrl?: string;
}

function buildArticleSchema(hero: HeroSlotData, canonicalUrl?: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: hero.title,
    description: hero.subtitle ?? undefined,
    author: {
      '@type': 'Person',
      name: hero.author,
      image: hero.authorAvatar ?? undefined,
    },
    datePublished: hero.publishedAt,
    dateModified: hero.updatedAt ?? hero.publishedAt,
    image: hero.coverImage ?? undefined,
    url: canonicalUrl ?? (typeof window !== 'undefined' ? window.location.href : undefined),
  };
}

function buildBreadcrumbSchema(breadcrumbs: Array<{ label: string; href: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.label,
      item: crumb.href,
    })),
  };
}

export default function SchemaOrg({ schemaTypes, hero, canonicalUrl }: Props) {
  useEffect(() => {
    const scripts: HTMLScriptElement[] = [];

    if (schemaTypes.includes('Article') && hero) {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.textContent = JSON.stringify(buildArticleSchema(hero, canonicalUrl));
      el.dataset.schemaType = 'Article';
      document.head.appendChild(el);
      scripts.push(el);
    }

    if (schemaTypes.includes('BreadcrumbList') && hero?.breadcrumbs?.length) {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.textContent = JSON.stringify(buildBreadcrumbSchema(hero.breadcrumbs));
      el.dataset.schemaType = 'BreadcrumbList';
      document.head.appendChild(el);
      scripts.push(el);
    }

    return () => scripts.forEach((s) => s.remove());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(schemaTypes), hero, canonicalUrl]);

  return null;
}
